import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Icon from 'components/global/Icon';
import Popper from 'components/global/Popper';
import Typography from 'components/global/Typography';

import useLocale from 'hooks/useLocale';

import { faCalendarAlt, faExclamationCircle, faTimes } from '@fortawesome/pro-solid-svg-icons';

import { FlatpickrWrapper } from './DateInput';
import TimeInput from './TimeInput';
import ViewField from './ViewField';
import * as S from './styled/DateWithTime.styled';
import { InputMessageContainer } from './styled/InputCommon.styled';

/**
 * @author Burak Ak
 * @description Date With Time component
 * @param {object} props
 * @param {Function} props.setValue
 * @param {Number} props.minuteIncrement
 * @param {Boolean} props.editableMinute
 * @param {String} props.placeholder
 * 
 * @example
 * <DateWithTime
 * 		name="deliverySlotDate"			
		value={watch('deliverySlotDate')}
		editableMinute={false}
		minuteIncrement={30}
		disabled={formMode === 'view' || userType === 'customerServiceUser'}
		setValue={(value) => {
			setValue('deliverySlotDate', value, { shouldValidate: true, shouldDirty: true });
		}}
	/>
 */

const DateWithTime = ({
	disabled = false,
	viewMode,
	value,
	setValue,
	errorMessage: errorMessageProp,
	className,
	placeholder = 'select',
	editableMinute = false,
	minuteIncrement = 30,
	name,
	style,
	css,
	...moreProps
}) => {
	const { getLocaleDate } = useLocale();
	const { t } = useTranslation();

	const [referenceElement, setReferenceElement] = useState(null);

	const formMethods = useFormContext();
	const errorMessage = formMethods?.getFieldState?.(name, formMethods?.formState)?.error?.message ?? errorMessageProp;

	const handleDelete = (e) => {
		e.stopPropagation();
		setValue({});
	};

	const parsedValue = useMemo(() => {
		if (!value) return;
		const date = value?.date ? `${getLocaleDate(value.date)}-` : '';
		const from = value?.from ? `${value.from}-` : '';
		const to = value?.to || '';
		return `${date}${from}${to}`;
	}, [value]);

	return (
		<>
			<S.InputContainer {...moreProps} viewmode={viewMode || disabled}>
				{viewMode || disabled ? (
					<ViewField value={parsedValue || '-'} disabled={disabled} />
				) : (
					<S.InputWrapper style={style} className={className} css={css}>
						<div className="leftIconContainer">
							<Icon icon={faCalendarAlt} color="var(--content-4)" />
						</div>

						<Typography
							ref={setReferenceElement}
							color={value && Object.keys(value).length !== 0 ? 'var(--black)' : 'var(--content-4)'}
							className={`${errorMessage ? 'error ' : ''}${className ?? ''}`}>
							{parsedValue || placeholder}
						</Typography>
						<div className="rightIconContainer">
							{value && Object.keys(value).length !== 0 && (
								<Icon
									icon={faTimes}
									onClick={(e) => handleDelete(e)}
									fontSize="var(--paragraph3-font-size)"
								/>
							)}
						</div>

						{referenceElement && (
							<Popper refElement={referenceElement} placement="top-start">
								{() => (
									<S.Content>
										<FlatpickrWrapper
											value={value?.date}
											setValue={(val) => {
												setValue({ ...value, date: val });
											}}
											inline
										/>
										<S.TimeContainer className="pt-4 pb-4">
											<TimeInput
												style={{ width: '40%' }}
												placeholder={t('from')}
												setValue={(val) => {
													setValue({ ...value, from: val });
												}}
												value={value?.from}
												minuteIncrement={minuteIncrement}
												editableMinute={editableMinute}
												time24hr={false}
											/>

											<Typography variant="h2" fontWeight="700">
												-
											</Typography>

											<TimeInput
												style={{ width: '40%' }}
												placeholder={t('dateWithTime.to')}
												setValue={(val) => {
													setValue({ ...value, to: val });
												}}
												value={value?.to}
												minuteIncrement={minuteIncrement}
												editableMinute={editableMinute}
												time24hr={false}
											/>
										</S.TimeContainer>
									</S.Content>
								)}
							</Popper>
						)}
					</S.InputWrapper>
				)}
			</S.InputContainer>
			{errorMessage && (
				<InputMessageContainer className="error-message-container date-time-input-error-message-container">
					<Icon icon={faExclamationCircle} color="var(--red)" size="x-small" />
					<Typography variant="caption" color="var(--red)" noWrap>
						{errorMessage}
					</Typography>
				</InputMessageContainer>
			)}
		</>
	);
};

export default DateWithTime;
